import { API_BASE_URL, getAuthToken } from './api';
import type {
  AssignResult,
  Assignee,
  AssistantTopic,
  ConversationDetail,
  ConversationList,
  InboxChanges,
  InboxFilter,
  NewTopicInput,
  ThreadMessage,
  ThreadPage,
  TopicPatch,
  WhatsAppAccount,
} from '../types/whatsapp';

// ApiError carries what the backend says: the HTTP status, the stable `code`
// and, for a send Meta rejected, the stored message in `body`.
// Callers decide by `code`, not by status: a 502 from the infrastructure has the
// same status as `send_failed` but no code.
export class ApiError extends Error {
  status: number;
  code: string;
  body: unknown;

  constructor(status: number, code: string, message: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }

  /** True when the session is gone or the subscription lapsed: stop retrying. */
  get isAuthFailure(): boolean {
    return this.status === 401 || this.status === 402;
  }
}

/** The message Meta rejected, from a `send_failed` error. */
export const failedMessageOf = (err: unknown): ThreadMessage | null => {
  if (!(err instanceof ApiError) || err.code !== 'send_failed') return null;
  const body = err.body as { message?: ThreadMessage } | null;
  return body?.message ?? null;
};

const parseBody = (text: string): unknown => {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

const toApiError = (status: number, body: unknown): ApiError => {
  const obj = body && typeof body === 'object' ? (body as { error?: string; code?: string }) : null;
  const message =
    status === 401
      ? 'Su sesión ha expirado. Por favor inicie sesión nuevamente'
      : obj?.error || 'Ocurrió un error inesperado. Intente nuevamente';
  return new ApiError(status, obj?.code ?? '', message, body);
};

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
}

const rawRequest = async (path: string, opts: RequestOptions = {}): Promise<Response> => {
  const token = await getAuthToken();
  return fetch(`${API_BASE_URL}${path}`, {
    method: opts.method ?? 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      ...(opts.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });
};

const request = async <T>(path: string, opts: RequestOptions = {}): Promise<T> => {
  const res = await rawRequest(path, opts);
  const body = parseBody(await res.text());
  if (!res.ok) throw toApiError(res.status, body);
  return body as T;
};

const query = (params: Record<string, string | number | boolean | undefined>): string => {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '' && v !== false) qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : '';
};

// ─── Account and assistant ────────────────────────────────────────────────────

export const getWhatsAppAccount = (signal?: AbortSignal) =>
  request<WhatsAppAccount>('/whatsapp/account', { signal });

export const setAssistantEnabled = (enabled: boolean) =>
  request<WhatsAppAccount>('/whatsapp/account', { method: 'PATCH', body: { bot_enabled: enabled } });

export const listTopics = async (): Promise<AssistantTopic[]> =>
  (await request<{ topics: AssistantTopic[] }>('/assistant/topics')).topics;

export const createTopic = (input: NewTopicInput) =>
  request<AssistantTopic>('/assistant/topics', { method: 'POST', body: input });

export const patchTopic = (key: string, patch: TopicPatch) =>
  request<AssistantTopic>(`/assistant/topics/${encodeURIComponent(key)}`, { method: 'PATCH', body: patch });

export const deleteTopic = (key: string) =>
  request<void>(`/assistant/topics/${encodeURIComponent(key)}`, { method: 'DELETE' });

// ─── Inbox ────────────────────────────────────────────────────────────────────

export interface ListParams {
  filter?: InboxFilter;
  unanswered?: boolean;
  q?: string;
  limit?: number;
  offset?: number;
}

export const listConversations = (params: ListParams = {}, signal?: AbortSignal) =>
  request<ConversationList>(
    `/whatsapp/conversations${query({
      filter: params.filter === 'all' ? undefined : params.filter,
      unanswered: params.unanswered,
      q: params.q?.trim(),
      limit: params.limit,
      offset: params.offset,
    })}`,
    { signal },
  );

export const getInboxChanges = (
  since: string,
  open?: { conversationId: number; afterMessageId: number },
  signal?: AbortSignal,
) =>
  request<InboxChanges>(
    `/whatsapp/inbox/changes${query({
      since,
      conversation_id: open?.conversationId,
      after_message_id: open?.afterMessageId,
    })}`,
    { signal },
  );

export const getConversation = (id: number, signal?: AbortSignal) =>
  request<ConversationDetail>(`/whatsapp/conversations/${id}`, { signal });

export const getThread = (id: number, before?: string, limit?: number, signal?: AbortSignal) =>
  request<ThreadPage>(`/whatsapp/conversations/${id}/messages${query({ before, limit })}`, { signal });

export interface SendResult {
  message: ThreadMessage;
  /** False when the server had already stored this client_message_id (nothing new was sent). */
  created: boolean;
}

/** Sends as the team. Throws an ApiError; for `send_failed` use failedMessageOf. */
export const sendMessage = async (
  conversationId: number,
  body: string,
  clientMessageId: string,
): Promise<SendResult> => {
  const res = await rawRequest(`/whatsapp/conversations/${conversationId}/messages`, {
    method: 'POST',
    body: { body, client_message_id: clientMessageId },
  });
  const parsed = parseBody(await res.text());
  if (!res.ok) throw toApiError(res.status, parsed);
  return { message: parsed as ThreadMessage, created: res.status === 201 };
};

export const retryMessage = (conversationId: number, messageId: number) =>
  request<ThreadMessage>(`/whatsapp/conversations/${conversationId}/messages/${messageId}/retry`, {
    method: 'POST',
  });

export const assignConversation = (conversationId: number, to: Assignee) =>
  request<AssignResult>(`/whatsapp/conversations/${conversationId}/assignment`, {
    method: 'POST',
    body: { assigned_to: to },
  });

export const markConversationRead = (conversationId: number) =>
  request<void>(`/whatsapp/conversations/${conversationId}/read`, { method: 'POST' });

export const linkPatient = (conversationId: number, patientId: number) =>
  request<ConversationDetail>(`/whatsapp/conversations/${conversationId}/patient`, {
    method: 'PUT',
    body: { patient_id: patientId },
  });

export const unlinkPatient = (conversationId: number) =>
  request<ConversationDetail>(`/whatsapp/conversations/${conversationId}/patient`, { method: 'DELETE' });

/** Downloads an attachment with the session header and returns it as a Blob. */
export const fetchMedia = async (conversationId: number, messageId: number): Promise<Blob> => {
  const res = await rawRequest(`/whatsapp/conversations/${conversationId}/messages/${messageId}/media`);
  if (!res.ok) throw toApiError(res.status, parseBody(await res.text()));
  return res.blob();
};
